import type { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { NonGatedThemeProvider } from '@lemon-io/ui-kit';
import { NextPageWithLayout } from 'pages/_app';

import { getServerProfile } from '@internal/api/profile/get-server-profile';
import { extractToken } from '@internal/api/system/utils';
import { isSignupIncomplete } from '@internal/components/app/constants';
import Routes from '@internal/config/routes';
import { ContactStatus } from '@internal/types/profile';
import {
  getSafeRedirectPath,
  REDIRECT_QUERY_PARAM,
} from '@internal/utils/safe-redirect';

const SignupPage = dynamic(() => import('@internal/components/signup'));

export const getServerSideProps: GetServerSideProps = async context => {
  const baseProps = {
    withoutLayout: true,
    lightBackground: true,
    appTheme: true,
  };

  if (extractToken(context)) {
    const profile = await getServerProfile(context);

    if (profile && !isSignupIncomplete(profile)) {
      const destination =
        getSafeRedirectPath(context.query[REDIRECT_QUERY_PARAM]) ??
        Routes.GetSetUp;
      return {
        redirect: { destination, permanent: false },
      };
    }

    if (profile) {
      return {
        props: {
          ...baseProps,
          resumeStep: profile.linkedin ? 'basics' : 'import',
          resumeApiKey: profile.apiKey,
          resumeQualified:
            profile.status_in_system !== ContactStatus.UNSUPPORTED_COUNTRY,
          resumeLinkedin: profile.linkedin || '',
          resumeGithub: profile.codesamples_link || '',
        },
      };
    }
  }

  return { props: baseProps };
};

type EscapeTheMatrixProps = {
  resumeStep?: 'import' | 'basics';
  resumeApiKey?: string;
  resumeQualified?: boolean;
  resumeLinkedin?: string;
  resumeGithub?: string;
};

const EscapeTheMatrixPage: NextPageWithLayout<EscapeTheMatrixProps> = ({
  resumeStep,
  resumeApiKey,
  resumeQualified,
  resumeLinkedin,
  resumeGithub,
}) => (
  <>
    <Head>
      <title>Become a Lemon.io Developer</title>
    </Head>
    <NonGatedThemeProvider>
      <SignupPage
        resumeStep={resumeStep}
        resumeApiKey={resumeApiKey}
        resumeQualified={resumeQualified}
        resumeLinkedin={resumeLinkedin}
        resumeGithub={resumeGithub}
      />
    </NonGatedThemeProvider>
  </>
);

export default EscapeTheMatrixPage;
